import { ref } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__ } from '@/locale'
import FileIcon from '../components/common/icons/FileIcon.vue'
import ImageFileIcon from '../components/common/icons/ImageFileIcon.vue'
import VideoFileIcon from '../components/common/icons/VideoFileIcon.vue'
import AudioFileIcon from '../components/common/icons/AudioFileIcon.vue'
import DocumentFileIcon from '../components/common/icons/DocumentFileIcon.vue'
import ArchiveFileIcon from '../components/common/icons/ArchiveFileIcon.vue'
import TextFileIcon from '../components/common/icons/TextFileIcon.vue'

const DESTROY_ATTACHMENT_MUTATION = gql`
  mutation DestroyAttachment($id: ID!) {
    destroyAttachment(id: $id) {
      success
      errors
    }
  }
`

/**
 * Composable for shared attachment list functionality
 * Handles file icons, image gallery, and delete operations
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onDelete - Callback when attachment is deleted
 * @returns {Object} - Reactive state and methods for attachment list
 */
export function useAttachmentList(options = {}) {
  const { onDelete = null } = options

  const showConfirmModal = ref(false)
  const attachmentToDelete = ref(null)
  const showImageGallery = ref(false)
  const galleryImages = ref([])
  const galleryInitialIndex = ref(0)

  const { mutate: deleteMutation } = useMutation(DESTROY_ATTACHMENT_MUTATION)

  const isImageAttachment = attachment => {
    if (!attachment.contentType) {
      const ext = attachment.filename.split('.').pop()?.toLowerCase()
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)
    }
    return attachment.contentType.startsWith('image/')
  }

  const openImageGallery = (attachment, allAttachments) => {
    const imageAttachments = allAttachments.filter(isImageAttachment)
    const imageIndex = imageAttachments.findIndex(img => img.id === attachment.id)

    if (imageIndex !== -1) {
      galleryImages.value = imageAttachments
      galleryInitialIndex.value = imageIndex
      showImageGallery.value = true
    }
  }

  const closeImageGallery = () => {
    showImageGallery.value = false
    galleryImages.value = []
    galleryInitialIndex.value = 0
  }

  const confirmDelete = attachment => {
    attachmentToDelete.value = attachment
    showConfirmModal.value = true
  }

  const cancelDelete = () => {
    showConfirmModal.value = false
    attachmentToDelete.value = null
  }

  const deleteAttachment = async () => {
    if (!attachmentToDelete.value) return

    try {
      const { data } = await deleteMutation({ id: attachmentToDelete.value.id })

      if (data?.destroyAttachment?.errors?.length) {
        const errorMsg = data.destroyAttachment.errors.join(', ')
        throw new Error(s__('Attachments|Failed to delete: %{error}').replace('%{error}', errorMsg))
      } else if (data?.destroyAttachment?.success) {
        if (onDelete) {
          onDelete(attachmentToDelete.value)
        }
      }
    } catch (error) {
      console.error('Delete error:', error)
      throw error
    } finally {
      cancelDelete()
    }
  }

  const getFileIconClass = filename => {
    const ext = filename.split('.').pop()?.toLowerCase()

    if (['pdf'].includes(ext)) return 'file-icon-pdf'
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'file-icon-image'
    if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return 'file-icon-video'
    if (['mp3', 'ogg', 'wav', 'm4a', 'flac'].includes(ext)) return 'file-icon-audio'
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'file-icon-archive'
    if (['doc', 'docx', 'txt'].includes(ext)) return 'file-icon-document'

    return 'file-icon-default'
  }

  const getFileIcon = (contentType, filename) => {
    if (contentType) {
      if (contentType.startsWith('image/')) return ImageFileIcon
      if (contentType.startsWith('video/')) return VideoFileIcon
      if (contentType.startsWith('audio/')) return AudioFileIcon
      if (contentType.includes('pdf')) return DocumentFileIcon
      if (contentType.includes('word') || contentType.includes('document')) return DocumentFileIcon
      if (contentType.includes('excel') || contentType.includes('spreadsheet'))
        return DocumentFileIcon
      if (contentType.includes('powerpoint') || contentType.includes('presentation'))
        return DocumentFileIcon
      if (
        contentType.includes('zip') ||
        contentType.includes('rar') ||
        contentType.includes('7z') ||
        contentType.includes('tar') ||
        contentType.includes('gzip')
      )
        return ArchiveFileIcon
      if (contentType.startsWith('text/')) return TextFileIcon
    }

    if (filename) {
      const ext = filename.split('.').pop()?.toLowerCase()
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return ImageFileIcon
      if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return VideoFileIcon
      if (['mp3', 'ogg', 'wav', 'm4a', 'flac'].includes(ext)) return AudioFileIcon
      if (['pdf'].includes(ext)) return DocumentFileIcon
      if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) return DocumentFileIcon
      if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return ArchiveFileIcon
      if (['txt', 'csv', 'md'].includes(ext)) return TextFileIcon
    }

    return FileIcon
  }

  const getTaskUrl = (task, chatId = null, fromId = null) => {
    if (!task || !task.iid || !task.projectId) return '#'
    const taskSegment = task.iid
    let url = `/projects/${task.projectId}/tasks/${taskSegment}`
    const params = []
    if (chatId) params.push(`chat_id=${encodeURIComponent(chatId)}`)
    if (fromId) params.push(`from_id=${encodeURIComponent(fromId)}`)
    if (params.length > 0) url += '?' + params.join('&')
    return url
  }

  return {
    // State
    showConfirmModal,
    attachmentToDelete,
    showImageGallery,
    galleryImages,
    galleryInitialIndex,

    // Methods
    isImageAttachment,
    openImageGallery,
    closeImageGallery,
    confirmDelete,
    cancelDelete,
    deleteAttachment,
    getFileIconClass,
    getFileIcon,
    getTaskUrl,
  }
}
